/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  createDialogRoute,
  DateTimeTypes,
  FormState,
  FormStateBuilder,
  LocalizationBusinessService,
  NumberTypes,
  State,
} from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  DialogService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  GanttTask,
  HOURS_END_OF_DAY,
  HOURS_START_OF_DAY,
  validateGanttTaskField,
} from '../../resources/gantt.model';
import { addDays, coalesce, isNotNil, normalizeDate, setHours, setMinutes } from '@gh/core-util';
import { getSlicedData } from '@gh/core-data';
import { filter$, map$ } from '@gh/rx/operators';
import {
  ContactRestService,
  isTaskToFinishRelationType,
  isTaskToStartRelationType,
  Resource,
  ScheduleTaskType,
  ScheduleType,
  TaskRelationType,
} from '@gh/builder-shared-data';
import { isResourceEditable } from '../../resources/schedule.model';
import { filter, isNil, max } from 'lodash';
import {
  GanttState,
  queryGanttAllTasks,
  queryGanttDateAddDuration,
  queryGanttIsDateAllowed,
  queryGanttIsDateValid,
  queryGanttLeftTrimToWorkTime,
  queryGanttRightTrimToWorkTime,
  queryGanttTask,
} from '../../gantt-chart.state';
import {
  GanttConfirmDeleteDialogComponent,
} from '../gantt-confirm-delete-dialog/gantt-confirm-delete-dialog.component';
import { TranslationService } from '@gh/core-mls';

export type GanttEditTaskDialogConfig = {
  task: GanttTask;
  gantt: GanttStatic;
  scheduleType?: ScheduleType;
  state: State<GanttState>;
  isTemplate?: boolean;
  isCombined?: boolean;
};

@Component({
  standalone: false,
             selector   : 'gh-gantt-edit-task-dialog',
             templateUrl: 'gantt-edit-task-dialog.component.html',
             styleUrls  : ['gantt-edit-task-dialog.component.scss'],
           })
export class GanttEditTaskDialogComponent implements OnInit, AfterViewInit, DialogInput<GanttEditTaskDialogConfig> {
  input: GanttEditTaskDialogConfig;
  form: FormState<GanttTask>;
  scheduleTypeEnum = ScheduleType;

  @ViewChild('durationInput') durationInputRef: ElementRef<HTMLInputElement>;

  get disableConfirmedControls(): boolean {
    return !this.input.scheduleType || this.input.scheduleType !== ScheduleType.Construction;
  }
  get showDeleteButton(): boolean {
    if (!this.input.task) {
      return false;
    }
    return !this.input.isCombined && (
      this.input.task?.canBeDeleted || (this.input.task?.canBeMarkedAsDeleted && !this.input.task?.markedAsDeleted)
    );
  }

  constructor(
    private dialogRef: MatDialogRef<any>,
    private dialogs: DialogService,
    private contactService: ContactRestService,
    private translation: TranslationService,
    private localizationBusinessService: LocalizationBusinessService,
    private formStateBuilder: FormStateBuilder,
  ) {
    this.isStartDateAllowed = this.isStartDateAllowed.bind(this);
    this.isEndDateAllowed = this.isEndDateAllowed.bind(this);
  }

  ngOnInit(): void {
    this.form = this.formStateBuilder.buildPlain(
      {
        value   : this.input.task,
        children: {
          startDate       : { required: !this.input.isTemplate, type: DateTimeTypes.ShortDate },
          endDate         : { required: !this.input.isTemplate, type: DateTimeTypes.ShortDate },
          duration        : { required: true, type: NumberTypes.Duration },
          completionPct   : { required: !this.input.isTemplate, type: NumberTypes.Percents },
          resource        : { enabled: !!this.input?.task && isResourceEditable(this.input?.task as any) },
          description     : { enabled: !!this.input?.task && isResourceEditable(this.input?.task as any) },
          confirmedWith   : { enabled: !!this.input?.scheduleType && this.input?.scheduleType === ScheduleType.Construction },
          confirmedOnDate : {
            enabled: !!this.input?.scheduleType && this.input?.scheduleType === ScheduleType.Construction,
            type   : DateTimeTypes.DateTime,
          },
          confirmedForDate: {
            enabled: !!this.input?.scheduleType && this.input?.scheduleType === ScheduleType.Construction,
            type   : DateTimeTypes.DateTime,
          },
        },
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.durationInputRef?.nativeElement && this.form) {
        const val = this.form.get('duration')?.value;
        this.durationInputRef.nativeElement.value = isNotNil(val)
          ? this.localizationBusinessService.formatDaysDuration(val)
          : '';
      }
    }, 0);
  }

  onApply(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close(this.form.submitValue);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  formattedDurationControlValue(): string {
    const val = this.form?.get('duration')?.value;
    return isNotNil(val)
      ? this.localizationBusinessService.formatDaysDuration(val)
      : '';
  }

  onDurationBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = this.form?.get('duration')?.value;
    const formatted = isNotNil(val)
      ? this.localizationBusinessService.formatDaysDuration(val)
      : '';
    setTimeout(() => {
      input.value = formatted;
    }, 0);
  }

  buttonClassName(): string {
    if (!this.form || !this.input.task) {
      return '';
    }
    const completion = coalesce(this.form.get('completionPct').value, 0);
    if (completion === 100) {
      return 'solid';
    } else if (completion > 0) {
      return 'tonal';
    }
    return 'outlined';
  }

  loadSuppliers($event: AutocompleteLoadMoreEvent): void {
    $event.load(this.contactService.findAllResourcesAsDataSource(
      {
        searchString: $event.query.filter,
        costCenterId: this.input.task['costCenterId'],
      },
    ).select($event.query).pipe(
      map$(getSlicedData),
      map$((items: Resource[]) => items.map((item) => ({
        name        : item.name,
        id          : item.contactId,
        isPreferred : item.isPreferred,
        isCcSupplier: item.isCcSupplier,
        isEmployee:   item.isEmployee,
      }))),
    ));
  }

  onStartDateChange(event: any): void {
    const date = event.target.value;
    if (!date || !this.input.state.project(queryGanttIsDateValid, date)) {
      return;
    }
    const task = {
      ...this.input.task,
      ...this.form.submitValue,
    };

    const roundDate = setMinutes(setHours(date, HOURS_START_OF_DAY), 0);
    // trim date by task predecessors
    const trimmedToPredecessorsDate = this.queryGanttLeftTrimToPredecessors(this.input.state, task, roundDate);
    // trim date to worktime
    const trimmedToWorkTimeDate = this.input.state.project(queryGanttLeftTrimToWorkTime, trimmedToPredecessorsDate);
    if (isNotNil(validateGanttTaskField(task, 'startDate', trimmedToWorkTimeDate))) {
      return;
    }
    const endDate = this.input.gantt.calculateEndDate(
      trimmedToWorkTimeDate, this.form.get('duration').value || 1, 'hour');
    this.form.get('endDate').setValue(endDate);
  }

  onEndDateChange(event: any): void {
    const date = event.target.value;
    if (!this.input.state.project(queryGanttIsDateValid, date)) {
      return;
    }
    const task = {
      ...this.input.task,
      ...this.form.submitValue,
    };
    const roundDate = setMinutes(setHours(date, HOURS_END_OF_DAY), 0);
    // trim date by task predecessors
    const trimmedToPredecessorsDate = this.queryGanttRightTrimToPredecessors(this.input.state, task, roundDate);
    const trimmedToWorkTimeDate = this.input.state.project(queryGanttRightTrimToWorkTime, trimmedToPredecessorsDate);
    const startDate = this.form.get('startDate').value;
    this.form.get('duration').setValue(this.input.gantt.calculateDuration(startDate, trimmedToWorkTimeDate));
  }

  isEndDateAllowed(date: Date): boolean {
    if (!this.form || !this.input.state) {
      return false;
    }
    const startDate = this.form.get('startDate').value;
    if (!startDate) {
      return false;
    }
    return normalizeDate(startDate) <= date
      && this.input.state.project(queryGanttIsDateAllowed, date);
  }

  isStartDateAllowed(date: Date): boolean {
    if (!this.form || !this.input.state) {
      return false;
    }
    return this.input.state.project(queryGanttIsDateAllowed, date);
  }

  onDurationChange(event: any): void {
    const duration = event.target.value;
    if (isNil(duration) || isNotNil(NumberTypes.HoursNotZero.validate(duration))) {
      return;
    }
    this.form.get('endDate').setValue(this.input.gantt.calculateEndDate(
      this.form.get('startDate').value,
      duration,
      'hour',
    ));
  }

  toggleCompletion(): void {
    const currentCompletionPct = coalesce(this.form.get('completionPct').value, 0);
    const control = this.form.get('completionPct');
    const half = 50;
    const done = 100;
    let nextCompletionPct;
    if (currentCompletionPct === half) {
      nextCompletionPct = 0;
    } else if (currentCompletionPct === done) {
      nextCompletionPct = half;
    } else {
      nextCompletionPct = done;
    }
    control.setValue(nextCompletionPct);
    control.markAsDirty();
  }

  onDelete(): void {
    if (this.input.task.canBeMarkedAsDeleted && !this.input.task.markedAsDeleted) {
      const relationsTask = this.input.state.project(queryGanttAllTasks)
                                .filter((item) => item.predecessors?.length &&
                                  item.predecessors?.find((predecessor) => predecessor.taskId === this.input.task.taskId),
                                );
      const taskNames = relationsTask.map((item) => item.name).join(', ') ;
      this.dialogs.confirmation(yesNoConfirmation(relationsTask.length
                                                    ? 'msg_sch_task_pred_mark_as_deleted'
                                                    : 'msg_sch_sum_task_mark_delete',
                                                  {
                                                    taskName: this.input.task.name,
                                                    taskNames,
                                                  })).pipe(
        filter$(yesAnswer))
          .subscribe(() =>  this.dialogRef.close('delete'));
      return;
    }
    const message = this.input.task.type === ScheduleTaskType.Summary
      ? 'msg_sch_sum_task_delete'
      : 'msg_sch_task_delete';
    this.dialogs.open(GanttConfirmDeleteDialogComponent, {
      data: {
        text: this.translation.get(message),
      },
    })
        .afterClosed()
        .subscribe((res) => {
          if (res === 'delete') {
            this.dialogRef.close('delete');
          }
        });
  }

  private queryGanttLeftTrimToPredecessors(state: State<GanttState>, task: GanttTask, date: Date): Date | undefined {
    const toStartPredecessors = filter(task.predecessors, ({ relation }) => isTaskToStartRelationType(relation));
    return max(toStartPredecessors
               .map(({ lag, taskId, relation }) => {
                 const predecessorTask = state.project(queryGanttTask, taskId);
                 return relation === TaskRelationType.StartToStart
                   ? state.project(queryGanttDateAddDuration, predecessorTask.startDate, lag)
                   : state.project(queryGanttDateAddDuration, predecessorTask.endDate, lag);
               })
               .concat([date]));
  }

  private queryGanttRightTrimToPredecessors(state: State<GanttState>, task: GanttTask, date: Date): Date | undefined {
    const toFinishPredecessors = filter(task.predecessors, ({ relation }) => isTaskToFinishRelationType(relation));
    return max(toFinishPredecessors
               .map(({ lag, taskId, relation }) => {
                 const predecessorTask = state.project(queryGanttTask, taskId);
                 return relation === TaskRelationType.FinishToFinish
                   ? addDays(predecessorTask.endDate, lag)
                   : addDays(predecessorTask.startDate, lag);
               })
               .concat([date]));
  }
}

export const ganttEditTaskDialogRoute = createDialogRoute(
  {
    component: GanttEditTaskDialogComponent,
    config   : { autoFocus: false },
  },
);
